import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import splinterlandsSettingsService from '../services/SplinterlandsSettingsService';

// Create context for the shopping cart
const CartContext = createContext();

// Custom hook to easily use the CartContext
export const useCart = () => useContext(CartContext);

// Local storage key for persistent cart items
const CART_STORAGE_KEY = 'splinterlands_cart_items';

/**
 * Fetches the latest Splinterlands blockchain settings
 * This helps ensure transactions are properly synchronized with the blockchain
 * Uses cached settings service to avoid redundant API calls
 * @returns {Promise<Object>} Latest blockchain settings
 */
const getLatestBlockchainSettings = async () => {
  try {
    // Use the cached settings service instead of direct API call
    const settings = await splinterlandsSettingsService.getSettings();
    return settings;
  } catch (error) {
    console.error('Error fetching blockchain settings:', error);
    return null;
  }
};



/**
 * CartProvider component that provides global state for the shopping cart
 * @param {object} props - React props with children
 * @returns {JSX.Element} - CartProvider element
 */
export const CartProvider = ({ children }) => {
  // State for cart items
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load cart items from localStorage on first render
  useEffect(() => {
    const savedItems = localStorage.getItem(CART_STORAGE_KEY);
    if (savedItems) {
      try {
        setCartItems(JSON.parse(savedItems));
      } catch (error) {
        console.error('Error loading cart items from localStorage:', error);
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    }
  }, []);

  // Save cart items to localStorage on every change
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  /**
   * Fetches detailed card information from the Splinterlands API
   * @param {string} uid - The card's UID
   * @returns {Promise<Object|null>} - The complete card object or null if error
   */
  const fetchCardDetailsByUid = async (uid) => {
    try {
      // Add cache buster parameter to ensure we get fresh data
      const cacheBuster = Math.random().toString(36).substring(2, 15);
      const response = await axios.get(`https://api2.splinterlands.com/cards/find?ids=${uid}&v=${cacheBuster}`);
      if (response.data && response.data.length > 0) {
        return response.data[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching card details:', error);
      return null;
    }
  };

  /**
   * Adds a card to the shopping cart
   * @param {Object} listing - The card listing to add
   * @param {Object} cardDetails - The details of the card
   */
  const addToCart = async (listing, cardDetails) => {
    // Check if the card is already in the cart
    if (cartItems.some(item => item.uid === listing.uid)) {
      return;
    }

    setLoading(true);

    try {
      // Make sure we have complete card information
      let fullCardData = listing;

      // If market_id is missing or other important fields, try to fetch them
      if (!listing.market_id || !listing.buy_price) {
        // Get additional card information from the Splinterlands API
        const apiCardDetails = await fetchCardDetailsByUid(listing.uid);
        
        if (apiCardDetails) {
          fullCardData = {
            ...listing,
            ...apiCardDetails,
            // Ensure these fields are present
            name: cardDetails?.name || apiCardDetails.details?.name || 'Unnamed Card',
            edition: apiCardDetails.edition || listing.edition || cardDetails?.edition,
            market_id: apiCardDetails.market_id || listing.market_id,
            buy_price: apiCardDetails.buy_price || listing.buy_price || listing.price
          };
        }
      }

      // Add the card with all relevant information
      const cartItem = {
        ...fullCardData,
        name: cardDetails?.name || fullCardData.name || (fullCardData.details?.name) || 'Unnamed Card',
        price: fullCardData.price || fullCardData.buy_price,
        market_id: fullCardData.market_id, // Ensure market_id is explicitly included
        timeAdded: new Date().toISOString(),
      };

      setCartItems(prevItems => [...prevItems, cartItem]);
    } catch (error) {
      console.error('Error adding item to cart:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Removes a card from the shopping cart
   * @param {string} uid - The UID of the card to remove
   */
  const removeFromCart = (uid) => {
    setCartItems(prevItems => prevItems.filter(item => item.uid !== uid));
  };

  /**
   * Clears the entire shopping cart
   */
  const clearCart = () => {
    setCartItems([]);
  };

  /**
   * Purchases a single card (for backward compatibility)
   * @param {Object} item - The item to purchase
   * @param {string} currency - The currency for the purchase ('DEC' or 'CREDITS')
   */
  const buyItem = async (item, currency) => {
    return await buyItems([item], currency);
  };

  /**
   * Purchases multiple cards in a single transaction
   * @param {Array} items - Array of items to purchase
   * @param {string} currency - The currency for the purchase ('DEC' or 'CREDITS')
   */
  const buyItems = async (items, currency) => {
    try {
      setLoading(true);
      
      // Validate input
      if (!items || !Array.isArray(items) || items.length === 0) {
        throw new Error('No items provided for purchase');
      }

      // Check if we have the necessary item information for all items
      for (const item of items) {
        if (!item || !item.uid) {
          throw new Error('Missing required card information for one or more purchases');
        }
        
        // Validate that market_id exists, it's crucial for purchase transactions
        if (!item.market_id) {
          throw new Error(`Missing market_id for card ${item.uid}. Cannot proceed with purchase`);
        }
      }

      // Validate if user is logged in through the AuthService
      const authService = window.authService || (await import('../services/AuthService')).default;
      if (!authService.isLoggedIn()) {
        throw new Error('You must be logged in to make purchases');
      }

      const username = authService.getCurrentUser()?.username;
      if (!username) {
        throw new Error('Unable to get username for transaction');
      }
      
      const blockchainSettings = window.splinterlandsBlockchainSettings || 
                                await getLatestBlockchainSettings();
      
      if (!blockchainSettings || !blockchainSettings.last_block) {
        console.warn('Could not fetch blockchain settings, continuing with purchase anyway');
      } else {
        window.splinterlandsBlockchainSettings = blockchainSettings;
      }
      
      const uids = items.map(item => item.uid).join(',');
      const cacheBuster = Math.random().toString(36).substring(2, 15);
      const verificationResponse = await axios.get(`https://api2.splinterlands.com/cards/find?ids=${uids}&v=${cacheBuster}`);
      
      if (!verificationResponse.data || !Array.isArray(verificationResponse.data)) {
        throw new Error('Unable to verify card availability');
      }
      
      // Check each card's availability and separate valid from invalid
      const verifiedCards = verificationResponse.data;
      const availableItems = [];
      const unavailableItems = [];
      
      for (const item of items) {
        const cardData = verifiedCards.find(card => card.uid === item.uid);
        if (!cardData) {
          unavailableItems.push({
            ...item,
            reason: 'Card no longer exists'
          });
        } else if (!cardData.market_id || cardData.market_listing_status !== 0) {
          unavailableItems.push({
            ...item,
            reason: 'No longer available for purchase'
          });
        } else {
          availableItems.push(item);
        }
      }
      
      // Remove unavailable items from cart immediately
      if (unavailableItems.length > 0) {
        const unavailableUids = unavailableItems.map(item => item.uid);
        setCartItems(prevItems => 
          prevItems.filter(cartItem => !unavailableUids.includes(cartItem.uid))
        );
      }
      
      // If no items are available, throw error with details
      if (availableItems.length === 0) {
        const unavailableNames = unavailableItems.map(item => item.name || 'Unknown Card').join(', ');
        throw new Error(`All items are no longer available: ${unavailableNames}. They have been removed from your cart.`);
      }
      
      // If some items are unavailable, create warning message but continue with available items
      let warningMessage = '';
      if (unavailableItems.length > 0) {
        const unavailableNames = unavailableItems.map(item => item.name || 'Unknown Card').join(', ');
        warningMessage = `${unavailableItems.length} item(s) were no longer available and removed from cart: ${unavailableNames}. `;
      }

      // Calculate total price using the verified card data from API
      // NOTE: Splinterlands API returns prices already in USD format
      let totalPriceUSD = 0;
      
      // Use the verified card data to get the actual current prices
      availableItems.forEach(item => {
        // Find the corresponding verified card data
        const verifiedCard = verifiedCards.find(card => card.uid === item.uid);
        
        if (verifiedCard && verifiedCard.buy_price) {
          // The buy_price from Splinterlands API is already in USD
          const currentPriceUSD = parseFloat(verifiedCard.buy_price);
          totalPriceUSD += currentPriceUSD;
        } else {
          // Fallback to item price if verified data is not available
          // These should also be in USD format from the market API
          const usdPrice = parseFloat(item.price || item.buy_price || 0);
          totalPriceUSD += usdPrice;
        }
      });

      // More aggressive rounding to avoid floating point precision issues
      // Round to 3 decimal places to match Splinterlands expectations
      totalPriceUSD = Math.round(totalPriceUSD * 1000) / 1000;
      
      // Add a small buffer to account for rounding errors (0.001 USD = ~1 DEC)
      // This ensures the transaction goes through even with minor price fluctuations
      totalPriceUSD += 0.005;

      // Debug logging to help troubleshoot price calculation issues
      console.log('Purchase calculation details:', {
        availableItemsCount: availableItems.length,
        totalPriceUSD,
        totalPriceRounded: totalPriceUSD,
        itemDetails: availableItems.map(item => {
          const verifiedCard = verifiedCards.find(card => card.uid === item.uid);
          return {
            uid: item.uid,
            market_id: item.market_id,
            cartPrice: item.price || item.buy_price,
            verifiedPrice: verifiedCard?.buy_price,
            name: item.name,
            note: 'All prices are in USD from Splinterlands API'
          };
        })
      });

      // Simplified purchase data - only essential info for Layer 1
      const purchaseData = {
        items: availableItems.map(item => item.market_id), // Array of market_ids for bulk purchase (only available items)
        price: totalPriceUSD, // Use exact calculated price without additional rounding
        currency: currency, // Original currency (DEC or CREDITS)
        market: "elmo.market",
        app: "elmo/1.0.0",
        n: Math.random().toString(36).substring(2, 10) // Random nonce
      };
      

      // Create the custom JSON operation for the Splinterlands market purchase
      const customJsonData = {
        id: "sm_market_purchase",
        json: JSON.stringify(purchaseData),
        required_auths: [username],
        required_posting_auths: []
      };

      // Execute the transaction with Hive Keychain
      return new Promise((resolve, reject) => {
        if (!window.hive_keychain) {
          reject(new Error('Hive Keychain not found. Please install the Hive Keychain browser extension.'));
          return;
        }

        window.hive_keychain.requestCustomJson(
          username,
          'sm_market_purchase',
          'Active',
          JSON.stringify(purchaseData),
          `Purchase ${availableItems.length} card${availableItems.length > 1 ? 's' : ''} for $${totalPriceUSD.toFixed(2)} USD (${currency})`,
          (response) => {
            if (response.success) {
              
              // Remove all purchased items from cart
              setCartItems(prevItems => 
                prevItems.filter(cartItem => 
                  !availableItems.some(purchasedItem => purchasedItem.uid === cartItem.uid)
                )
              );
              
              // Create success message with warning if some items were removed
              const successMessage = `${warningMessage}Successfully purchased ${availableItems.length} card${availableItems.length > 1 ? 's' : ''} for $${totalPriceUSD.toFixed(2)} USD`;
              
              resolve({
                success: true,
                message: successMessage,
                transaction: response,
                purchasedItems: availableItems,
                removedItems: unavailableItems
              });
            } else {
              console.error('Bulk purchase transaction failed:', response);
              reject(new Error(response.message || 'Transaction failed. Please try again.'));
            }
          }
        );
      });
    } catch (error) {
      console.error('Error during bulk purchase:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // The value provided to the context
  const contextValue = {
    cartItems,
    addToCart,
    removeFromCart,
    clearCart,
    buyItem,
    buyItems,
    loading
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
